import React, { useState, useEffect } from 'react'
import "./Dashboard.scss";
import API from '../../utilities/API';


function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chartFilter, setChartFilter] = useState<'day' | 'week' | 'month'>('week');
  const api = new API();

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        // Charger les statistiques
        const statsResponse = await api.apiData('get', 'recepisse/dashboard/stats');
        if (statsResponse.status === 200) {
          setStats(statsResponse.data);
        }

        // Charger les données du graphique
        const chartResponse = await api.apiData('get', `recepisse/dashboard/chart?period=${chartFilter}`);
        if (chartResponse.status === 200) {
          setChartData(chartResponse.data);
        }

        // Charger l'activité récente
        const activityResponse = await api.apiData('get', 'recepisse/dashboard/activity');
        if (activityResponse.status === 200) {
          setRecentActivity(activityResponse.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [chartFilter]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const renderChangeIndicator = (changePercent: number) => {
    const isPositive = changePercent >= 0;
    return (
      <div className={`stat-card__change ${isPositive ? 'stat-card__change--up' : 'stat-card__change--down'}`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points={isPositive ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
        </svg>
        <span>{Math.abs(changePercent)}%</span>
        <span className='stat-card__period'>depuis le mois dernier</span>
      </div>
    );
  };

  const getMaxChartValue = () => {
    const validData = getValidChartData();
    if (validData.length === 0) return 100;
    return Math.max(...validData.map((item: any) => item.count), 1);
  };

  const handleFilterChange = (filter: 'day' | 'week' | 'month') => {
    setChartFilter(filter);
  };

  const getChartTitle = () => {
    switch (chartFilter) {
      case 'day': return 'Statistiques des 8 dernières heures';
      case 'week': return 'Statistiques des 7 derniers jours';
      case 'month': return `Statistiques par mois - ${new Date().getFullYear()}`;
      default: return 'Statistiques';
    }
  };

  const isValidDate = (dateString: string) => {
    // Gérer le format YYYY-MM pour les mois
    if (chartFilter === 'month' && /^\d{4}-\d{2}$/.test(dateString)) {
      const [year, month] = dateString.split('-').map(Number);
      return year > 0 && month >= 1 && month <= 12;
    }
    
    // Gérer le format YYYY-MM-DD-HH pour les heures
    if (chartFilter === 'day' && /^\d{4}-\d{2}-\d{2}-\d{2}$/.test(dateString)) {
      const date = new Date(dateString.replace(/-/g, '/') + ':00:00');
      return !isNaN(date.getTime());
    }
    
    // Validation par défaut pour les autres formats
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const getDateLabel = (dateString: string) => {
    const date = new Date(dateString);
    if (!isValidDate(dateString)) {
      return null;
    }
    
    switch (chartFilter) {
      case 'day':
        const dateHour = new Date(dateString.replace(/-/g, '/') + ':00:00');
        return dateHour.toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      case 'week':
        const dateWeek = new Date(dateString);
        return dateWeek.toLocaleDateString('fr-FR', { weekday: 'short' });
      case 'month':
        // Pour le format YYYY-MM, convertir en nom de mois français
        const [year, month] = dateString.split('-');
        const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        return monthDate.toLocaleDateString('fr-FR', { month: 'short' });
      default:
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    }
  };

  const getValidChartData = () => {
    if (!chartData?.data) return [];
    return chartData.data.filter((item: any) => isValidDate(item._id));
  };

  return (
    <div className='dashboard'>
      <div className='dashboard__header'>
        <div className='dashboard__header-left'>
          <h1>Tableau de bord</h1>
          <p>Vue d'ensemble de votre activité</p>
        </div>
        <div className='dashboard__header-right'>
          <div className='dashboard__search'>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input type="text" placeholder="Rechercher..." />
          </div>
          <button className='dashboard__notification'>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          </button>
        </div>
      </div>

      <div className='dashboard__stats'>
        {isLoading ? (
          <div className="dashboard__loading">
            <p>Chargement des statistiques...</p>
          </div>
        ) : stats ? (
          <>
            <div className='stat-card stat-card--primary'>
              <div className='stat-card__content'>
                <div className='stat-card__label'>Total Récépissés</div>
                <div className='stat-card__value'>{formatNumber(stats.totalRecepisses)}</div>
                {renderChangeIndicator(stats.changePercent)}
              </div>
              <div className='stat-card__icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
            </div>

            <div className='stat-card'>
              <div className='stat-card__content'>
                <div className='stat-card__label'>Ce mois</div>
                <div className='stat-card__value'>{formatNumber(stats.recepissesThisMonth)}</div>
                <div className='stat-card__change stat-card__change--neutral'>
                  <span>Récépissés ce mois</span>
                </div>
              </div>
              <div className='stat-card__icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
            </div>

            <div className='stat-card'>
              <div className='stat-card__content'>
                <div className='stat-card__label'>Utilisateurs</div>
                <div className='stat-card__value'>{formatNumber(stats.totalUsers)}</div>
                <div className='stat-card__change stat-card__change--neutral'>
                  <span>Utilisateurs actifs</span>
                </div>
              </div>
              <div className='stat-card__icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
            </div>

            <div className='stat-card'>
              <div className='stat-card__content'>
                <div className='stat-card__label'>Catégories</div>
                <div className='stat-card__value'>{stats.categoriesStats?.length || 0}</div>
                <div className='stat-card__change stat-card__change--neutral'>
                  <span>Types de véhicules</span>
                </div>
              </div>
              <div className='stat-card__icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
            </div>
          </>
        ) : (
          <div className="dashboard__error">
            <p>Erreur lors du chargement des statistiques</p>
          </div>
        )}
      </div>

      {/* Section Graphique et Activité */}
      <div className='dashboard__content'>
        <div className='dashboard__chart-section'>
          <div className='chart-card'>
            <div className='chart-card__header'>
              <h3>{getChartTitle()}</h3>
              <div className='chart-card__actions'>
                <button 
                  className={chartFilter === 'day' ? 'active' : ''}
                  onClick={() => handleFilterChange('day')}
                >
                  8h
                </button>
                <button 
                  className={chartFilter === 'week' ? 'active' : ''}
                  onClick={() => handleFilterChange('week')}
                >
                  7 jours
                </button>
                <button 
                  className={chartFilter === 'month' ? 'active' : ''}
                  onClick={() => handleFilterChange('month')}
                >
                  Mois
                </button>
              </div>
            </div>
            <div className='chart-card__body'>
              {chartData?.data ? (
                <>
                  <div className='chart-placeholder'>
                    {getValidChartData().map((item: any, index: number) => {
                      const height = (item.count / getMaxChartValue()) * 100;
                      const dateLabel = getDateLabel(item._id);
                      return (
                        <div 
                          key={index} 
                          className='chart-bar' 
                          style={{height: `${height}%`}}
                          title={`${item.count} récépissés - ${dateLabel}`}
                        ></div>
                      );
                    })}
                  </div>
                  <div className='chart-labels'>
                    {getValidChartData().map((item: any, index: number) => {
                      const dateLabel = getDateLabel(item._id);
                      return dateLabel ? (
                        <span key={index}>{dateLabel}</span>
                      ) : null;
                    })}
                  </div>
                </>
              ) : (
                <div className="chart-loading">
                  <p>Chargement du graphique...</p>
                </div>
              )}
            </div>
          </div>

          <div className='activity-card'>
            <div className='activity-card__header'>
              <h3>Activité Récente</h3>
            </div>
            <div className='activity-card__list'>
              {recentActivity.length > 0 ? (
                recentActivity.map((activity: any, index: number) => (
                  <div key={index} className='activity-item'>
                    <div className='activity-item__icon'>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                      </svg>
                    </div>
                    <div className='activity-item__content'>
                      <div className='activity-item__title'>{activity.title}</div>
                      <div className='activity-item__time'>{activity.time}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-loading">
                  <p>Chargement de l'activité...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard